import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { mediaUrl, formatBlogDate } from '../utils/blogUtils';
import { mergeFeaturedWithApiPosts, getPinnedFeaturedPosts } from '../config/featuredBlogPosts';
import {
  BLOG_CATEGORIES,
  getCategoryById,
  getCategoryLabel,
  resolvePostBlogCategory,
} from '../config/blogCategories';
import BlogCategoryNav from './BlogCategoryNav';
import BoatRaceLanguageSwitcher from './BoatRaceLanguageSwitcher';
import { BOAT_RACE_LOCALES } from '../config/boatRacePostUtils';
import { WELLNESS_HUB_SLUG } from '../config/wellness/segments';
import { GLOBAL_WELLNESS_HUB_SLUG } from '../config/wellness/global/segments';
import { isSkillTrainingGuide, sortPostsByDate } from '../config/blogPostUtils';
import { getDefaultBlogListMeta, setPageMeta, resetPageMeta } from '../utils/pageMeta';
import { SITE_NAME } from '../config/siteBranding';
import AppSwitcher from './AppSwitcher';
import './Blog.css';

const WELLNESS_PLAN_FILTERS = [
  { value: 'all', label: 'All plans' },
  { value: 'hub', label: 'Wellness hub' },
  { value: 'vegetarian-diet', label: 'Vegetarian diet' },
  { value: 'nonveg-diet', label: 'Non-veg diet' },
];

const PREVIEW_PER_SECTION = 5;
const LATEST_POSTS_COUNT = 6;

function applyWellnessFilters(posts, { planFilter, genderFilter, ageFilter }) {
  return posts.filter((post) => {
    const meta = post.wellnessMeta;
    const isWellness = Boolean(meta);
    if (planFilter === 'hub' && meta?.planType !== 'hub') return false;
    if (planFilter !== 'all' && planFilter !== 'hub' && meta?.planType !== planFilter) {
      return false;
    }
    if (genderFilter !== 'all') {
      if (!isWellness || meta.planType === 'hub') {
        return planFilter === 'all' || planFilter === 'hub';
      }
      if (meta.gender !== genderFilter) return false;
    }
    if (ageFilter !== 'all') {
      if (!isWellness || meta.planType === 'hub') {
        return planFilter === 'all' || planFilter === 'hub';
      }
      if (meta.ageKey !== ageFilter) return false;
    }
    return true;
  });
}

function BlogPostCard({ post }) {
  const categoryId = resolvePostBlogCategory(post);
  return (
    <li>
      <Link
        to={`/blog/${post.slug}`}
        className={`blog-card${post.featured ? ' blog-card--featured' : ''}`}
      >
        {post.coverImageUrl && (
          <img
            src={mediaUrl(post.coverImageUrl)}
            alt=""
            className="blog-card__cover"
            loading="lazy"
          />
        )}
        <div className="blog-card__body">
          <p className="blog-card__meta">
            <span className={`blog-card__category blog-card__category--${categoryId}`}>
              {getCategoryLabel(categoryId)}
            </span>
            {post.featured && <span className="blog-card__featured-badge">Featured</span>}
            {post.pinned && <span className="blog-card__featured-badge">Spotlight</span>}
            {post.audience && <span>{post.audience}</span>}
            {post.locale && (
              <span className="blog-card__locale">
                {BOAT_RACE_LOCALES[post.locale]?.shortLabel || post.locale.toUpperCase()}
              </span>
            )}
            {post.publishedAt && <span>{formatBlogDate(post.publishedAt)}</span>}
          </p>
          <h2>{post.title}</h2>
          {post.excerpt && <p className="blog-card__excerpt">{post.excerpt}</p>}
          <span className="blog-card__read">Read more →</span>
        </div>
      </Link>
    </li>
  );
}

function BlogPostList({ posts }) {
  if (!posts.length) return null;
  return (
    <ul className="blog-list">
      {posts.map((post) => (
        <BlogPostCard key={post.id} post={post} />
      ))}
    </ul>
  );
}

const BlogList = () => {
  const { categoryId: routeCategoryId } = useParams();
  const activeCategoryId = routeCategoryId || 'all';
  const invalidCategory =
    Boolean(routeCategoryId) && !BLOG_CATEGORIES.some((c) => c.id === routeCategoryId);

  const { isAuthenticated, user, logout } = useAuth();
  const [posts, setPosts] = useState(() => mergeFeaturedWithApiPosts([]));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [genderFilter, setGenderFilter] = useState('all');
  const [ageFilter, setAgeFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [localeFilter, setLocaleFilter] = useState('all');

  const showWellnessFilters =
    activeCategoryId === 'food-diet' || activeCategoryId === 'exercise';
  const showBoatRaceFilters = activeCategoryId === 'boat-races';

  const categoryPosts = useMemo(() => {
    const scoped =
      activeCategoryId === 'all' ? posts : posts.filter((p) => resolvePostBlogCategory(p) === activeCategoryId);
    return sortPostsByDate(scoped);
  }, [posts, activeCategoryId]);

  const filteredPosts = useMemo(() => {
    let result = categoryPosts;
    if (showBoatRaceFilters && localeFilter !== 'all') {
      result = result.filter((p) => p.locale === localeFilter);
    }
    if (!showWellnessFilters) return result;
    if (activeCategoryId === 'exercise') {
      const guides = result.filter((p) => isSkillTrainingGuide(p));
      const trainingPlans = applyWellnessFilters(
        result.filter((p) => p.wellnessMeta?.planType === 'training'),
        { planFilter: 'training', genderFilter, ageFilter }
      );
      return sortPostsByDate([...guides, ...trainingPlans]);
    }
    return applyWellnessFilters(result, { planFilter, genderFilter, ageFilter });
  }, [
    categoryPosts,
    showWellnessFilters,
    showBoatRaceFilters,
    localeFilter,
    activeCategoryId,
    planFilter,
    genderFilter,
    ageFilter,
  ]);

  const groupedSections = useMemo(() => {
    if (activeCategoryId !== 'all') return null;
    return BLOG_CATEGORIES.filter((c) => c.id !== 'all')
      .map((cat) => {
        const items = sortPostsByDate(posts.filter((p) => resolvePostBlogCategory(p) === cat.id));
        return { category: cat, items };
      })
      .filter((s) => s.items.length > 0);
  }, [posts, activeCategoryId]);

  const pinnedPosts = useMemo(() => {
    const pinnedSlugs = new Set(getPinnedFeaturedPosts().map((post) => post.slug));
    return sortPostsByDate(posts.filter((post) => pinnedSlugs.has(post.slug)));
  }, [posts]);

  const latestPosts = useMemo(() => {
    if (activeCategoryId !== 'all') return [];
    const pinnedSlugs = new Set(pinnedPosts.map((post) => post.slug));
    return sortPostsByDate(posts.filter((post) => !pinnedSlugs.has(post.slug))).slice(0, LATEST_POSTS_COUNT);
  }, [posts, activeCategoryId, pinnedPosts]);

  const loadPosts = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await api.getPublishedPosts();
      setPosts(mergeFeaturedWithApiPosts(Array.isArray(data) ? data : []));
    } catch (err) {
      setPosts(mergeFeaturedWithApiPosts([]));
      setError(err.message || 'Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  useEffect(() => {
    setPlanFilter('all');
    setGenderFilter('all');
    setAgeFilter('all');
    setLocaleFilter('all');
  }, [activeCategoryId]);

  useEffect(() => {
    if (invalidCategory) return undefined;
    const category = getCategoryById(activeCategoryId);
    const meta = getDefaultBlogListMeta(category?.label);
    setPageMeta(meta);
    return () => resetPageMeta();
  }, [activeCategoryId, invalidCategory]);

  if (invalidCategory) {
    return <Navigate to="/blog" replace />;
  }

  const activeCategory = getCategoryById(activeCategoryId);

  return (
    <div className="blog-page">
      <header className="blog-page__header">
        <div className="blog-page__header-top">
          <div className="blog-page__brand-block">
            <Link to="/" className="blog-page__home-link">
              {SITE_NAME}
            </Link>
            <AppSwitcher activeAppId="blog" variant="compact" className="blog-page__apps" />
          </div>
          <nav className="blog-page__auth">
            {isAuthenticated ? (
              <>
                <span>{user?.name}</span>
                <button type="button" onClick={logout}>
                  Log out
                </button>
              </>
            ) : (
              <>
                <Link to="/login">Log in</Link>
                <Link to="/register">Join</Link>
              </>
            )}
          </nav>
        </div>
        <h1>{activeCategoryId === 'all' ? 'Blog' : activeCategory.label}</h1>
        <p>{activeCategory.description}</p>
        {activeCategoryId === 'food-diet' && (
          <p className="blog-page__hub-link">
            <Link to={`/blog/${GLOBAL_WELLNESS_HUB_SLUG}`}>Open global healthy eating guide →</Link>
            {' · '}
            <Link to={`/blog/${WELLNESS_HUB_SLUG}`}>Ohio Indian meal plans →</Link>
          </p>
        )}
      </header>

      <BlogCategoryNav />

      {showBoatRaceFilters && (
        <section className="blog-filters" aria-label="Filter boat race posts">
          <BoatRaceLanguageSwitcher
            variant="filter"
            localeFilter={localeFilter}
            onLocaleFilterChange={setLocaleFilter}
          />
        </section>
      )}

      {showWellnessFilters && (
        <section className="blog-filters" aria-label="Filter wellness posts">
          <div className="blog-filters__row">
            {activeCategoryId === 'food-diet' && (
              <label>
                Plan type
                <select value={planFilter} onChange={(e) => setPlanFilter(e.target.value)}>
                  {WELLNESS_PLAN_FILTERS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label>
              Gender
              <select value={genderFilter} onChange={(e) => setGenderFilter(e.target.value)}>
                <option value="all">All</option>
                <option value="women">Women</option>
                <option value="men">Men</option>
              </select>
            </label>
            <label>
              Age
              <select value={ageFilter} onChange={(e) => setAgeFilter(e.target.value)}>
                <option value="all">All</option>
                <option value="18-34">18–34</option>
                <option value="35-49">35–49</option>
                <option value="50-plus">50+</option>
              </select>
            </label>
          </div>
        </section>
      )}

      {loading && posts.length === 0 && <p className="blog-page__status">Loading posts…</p>}
      {error && posts.every((p) => p.featured) && (
        <p className="blog-page__status blog-page__status--error">
          Club posts unavailable right now. Featured stories are still below.
        </p>
      )}

      {!loading && filteredPosts.length === 0 && posts.length > 0 && (
        <p className="blog-page__status">No posts match these filters.</p>
      )}

      {!loading && posts.length === 0 && (
        <p className="blog-page__status">No published posts yet. Check back soon.</p>
      )}

      {activeCategoryId === 'all' && (pinnedPosts.length > 0 || latestPosts.length > 0 || groupedSections) && (
        <div className="blog-sections">
          {pinnedPosts.length > 0 && (
            <section className="blog-section blog-section--pinned" aria-labelledby="blog-sec-pinned">
              <div className="blog-section__head">
                <h2 id="blog-sec-pinned">Featured story</h2>
              </div>
              <p className="blog-section__desc">Tournament recap with squad photos, awards, and full results.</p>
              <BlogPostList posts={pinnedPosts} />
            </section>
          )}

          {latestPosts.length > 0 && (
            <section className="blog-section blog-section--latest" aria-labelledby="blog-sec-latest">
              <div className="blog-section__head">
                <h2 id="blog-sec-latest">Latest posts</h2>
              </div>
              <p className="blog-section__desc">Newest stories across all categories.</p>
              <BlogPostList posts={latestPosts} />
            </section>
          )}

          {groupedSections?.map(({ category, items }) => (
            <section key={category.id} className="blog-section" aria-labelledby={`blog-sec-${category.id}`}>
              <div className="blog-section__head">
                <h2 id={`blog-sec-${category.id}`}>{category.label}</h2>
                <Link to={category.path} className="blog-section__view-all">
                  View all ({items.length}) →
                </Link>
              </div>
              <p className="blog-section__desc">{category.description}</p>
              <BlogPostList posts={items.slice(0, PREVIEW_PER_SECTION)} />
            </section>
          ))}
        </div>
      )}

      {activeCategoryId !== 'all' && filteredPosts.length > 0 && (
        <BlogPostList posts={filteredPosts} />
      )}
    </div>
  );
};

export default BlogList;
